import { z } from 'zod';

const requiredText = z.string().trim().min(1, '필수 입력값입니다');
/** 임시저장본에서 아직 비어있어도 되는 칸 */
const optionalText = z.string().trim().default('');
const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const slugMessage = '영문 소문자, 숫자, 하이픈만 사용할 수 있습니다';
const slug = requiredText.regex(slugPattern, slugMessage);
const draftSlug = z.union([
  z.literal(''),
  z.string().trim().regex(slugPattern, slugMessage),
]);
const referenceUrl = z.union([z.literal(''), z.url({ protocol: /^https?$/ })]);
/**
 * A calendar date as `YYYY-MM-DD`, or empty when the author has not set one.
 * Stored as a plain date rather than a timestamp because the page shows the day
 * only, and a date input produces exactly this format.
 */
const optionalIsoDate = z
  .union([z.literal(''), z.iso.date('YYYY-MM-DD 형식으로 입력해 주세요')])
  .default('');

/**
 * ### INDUSTRIAL_CASE_SOLUTIONS
 *
 * Product lines a case can belong to. Drives the list-page tabs, the related
 * case selection, and which Product the structured data points at.
 */
export const INDUSTRIAL_CASE_SOLUTIONS = ['industrial', 'safety'] as const;

/**
 * ### IndustrialCaseSolution
 *
 * One of {@link INDUSTRIAL_CASE_SOLUTIONS}.
 */
export type IndustrialCaseSolution = (typeof INDUSTRIAL_CASE_SOLUTIONS)[number];

/**
 * ### INDUSTRIAL_CASE_OUTCOME_LIMIT
 *
 * How many supporting metrics a published case may carry.
 *
 * The limit dropped from six to three so every metric on the page can carry its
 * definition, scope, and source without the sidebar turning into a table.
 * Documents published under the old limit are not rejected: the draft schema
 * still accepts up to {@link INDUSTRIAL_CASE_LEGACY_OUTCOME_LIMIT} so they open
 * in the editor with every row intact, and the public renderer shows only the
 * first three. Publishing again requires the author to trim the list.
 */
export const INDUSTRIAL_CASE_OUTCOME_LIMIT = 3;

/**
 * ### INDUSTRIAL_CASE_LEGACY_OUTCOME_LIMIT
 *
 * The outcome limit that applied before {@link INDUSTRIAL_CASE_OUTCOME_LIMIT}.
 * Only the draft schema uses it, so legacy documents remain readable.
 */
export const INDUSTRIAL_CASE_LEGACY_OUTCOME_LIMIT = 6;

/**
 * ### INDUSTRIAL_CASE_TASK_LIMIT
 *
 * Upper bound for the customer tasks list.
 */
export const INDUSTRIAL_CASE_TASK_LIMIT = 6;

/**
 * ### INDUSTRIAL_CASE_CONDITION_LIMIT
 *
 * Upper bound shared by the site-condition and system-setup lists.
 */
export const INDUSTRIAL_CASE_CONDITION_LIMIT = 8;

/**
 * ### industrialCaseAccuracyPointSchema
 *
 * Validates the points retained for deprecated in-document figures.
 * New authoring inserts media into narrative content instead of creating these
 * figures, but old documents must remain readable.
 */
export const industrialCaseAccuracyPointSchema = z.object({
  value: z.number().min(0).max(100),
  displayValue: requiredText,
  label: requiredText,
});

/**
 * ### industrialCaseFigureSchema
 *
 * Accepts the legacy figure payload while new documents use narrative media.
 */
export const industrialCaseFigureSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('accuracy'),
    points: z.array(industrialCaseAccuracyPointSchema).min(2).max(6),
  }),
  z.object({ kind: z.literal('flow') }),
]);

/**
 * 게시본과 임시저장본은 같은 모양이고 "다 채웠는지"만 다르다.
 *
 * 임시저장은 말 그대로 초안이라 빈 칸을 그대로 저장하고 다시 열 수 있어야 한다.
 * 그래서 한 곳에서 두 스키마를 함께 만든다 — 칸을 하나 늘릴 때 두 군데를
 * 고치게 되면 게시본만 검사하거나 초안만 열리는 어긋남이 생긴다.
 *
 * Fields added after the first release are optional with defaults in *both*
 * modes, published included. Documents already on the board predate them, and
 * the public site reads the board through the draft schema, so a required new
 * field would either 404 every existing case or force a data migration. The
 * editor steers authors toward filling them with "recommended" warnings instead
 * of blocking publication. The only exceptions are the two disclosure
 * confirmations, which publishing genuinely must not proceed without.
 */
function createIndustrialCaseDocumentShape(mode: 'published' | 'draft') {
  const isDraft = mode === 'draft';
  const text = isDraft ? optionalText : requiredText;
  /** 초안은 항목이 아예 없어도 된다 */
  const least = isDraft ? 0 : 1;
  /**
   * A metric with the evidence behind it. `definition`, `scope`, and `source`
   * are recommended rather than required so documents that predate them stay
   * publishable; the editor warns when `source` is empty.
   */
  const metric = z.object({
    value: text,
    label: text,
    definition: optionalText,
    scope: optionalText,
    source: optionalText,
  });
  /**
   * A recommended list: it may be empty, but a row the author did add must be
   * filled before publishing, the same rule that applies to challenges.
   */
  const recommendedList = (max: number) => z.array(text).max(max).default([]);
  /**
   * Publishing requires an explicit `true`; a draft may leave it unchecked. A
   * legacy document without the field reads as unconfirmed rather than failing
   * to parse, so re-publishing it asks the author to confirm once.
   */
  const confirmation = isDraft
    ? z.boolean().default(false)
    : z
        .boolean()
        .default(false)
        .refine((value) => value, '게시 전에 확인이 필요합니다');

  return {
    /** 이 값이 문서를 우리 형식으로 알아보는 유일한 표식이라 초안에서도 반드시 있어야 한다 */
    schemaVersion: z.literal(1),
    slug: isDraft ? draftSlug : slug,
    language: z.enum(['ko', 'en']),
    /** Product line; defaults to Industrial because every existing case is one */
    solution: z.enum(INDUSTRIAL_CASE_SOLUTIONS).default('industrial'),
    caseNumber: text,
    customerName: text,
    title: text,
    meta: text,
    targetSound: text,
    /** Classification shown in the hero meta and used by list cards and filters */
    industry: optionalText,
    process: optionalText,
    inspectionTarget: optionalText,
    summary: text,
    heroImage: z.object({ pc: text, mobile: text }),
    primaryMetric: metric,
    overview: text,
    /** What the customer set out to solve */
    tasks: recommendedList(INDUSTRIAL_CASE_TASK_LIMIT),
    challenges: z.array(text).min(least).max(8),
    /** Site constraints such as noise level, Cycle Time, line count, mounting limits */
    siteConditions: recommendedList(INDUSTRIAL_CASE_CONDITION_LIMIT),
    /** Deployment shape: microphones, DAQ, analysis server location, PLC or MES links */
    systemSetup: recommendedList(INDUSTRIAL_CASE_CONDITION_LIMIT),
    outcomes: z
      .array(metric)
      .min(least)
      .max(
        isDraft
          ? INDUSTRIAL_CASE_LEGACY_OUTCOME_LIMIT
          : INDUSTRIAL_CASE_OUTCOME_LIMIT,
      ),
    /** Optional comparison; the page shows it only when both sides are filled */
    beforeAfter: z
      .object({ before: optionalText, after: optionalText })
      .default({ before: '', after: '' }),
    /** How the results were measured, and what they do not claim */
    measurementConditions: optionalText,
    limitations: optionalText,
    narrative: z
      .array(
        z.object({
          // id 는 본문 인용이 가리키는 키라서 편집 화면이 항상 만들어 넣는다
          id: requiredText,
          heading: text,
          bodyHtml: text,
        }),
      )
      .min(least)
      .max(20),
    /**
     * @deprecated 그래프는 본문 에디터에 이미지로 삽입한다.
     * 이 필드를 저장한 기존 문서를 계속 열기 위해서만 남겨둔 형태다.
     */
    figure: industrialCaseFigureSchema.optional(),
    references: z
      .array(
        z.object({
          id: requiredText,
          title: text,
          source: text,
          /**
           * 인용 미리보기(툴팁)에 노출되는 설명.
           * 이 필드가 없던 문서도 계속 열 수 있도록 기본값을 둔다.
           */
          description: optionalText,
          url: referenceUrl,
          /**
           * `url` 이 직접 올린 파일을 가리킬 때 화면에 보여줄 파일 이름.
           * 링크를 직접 입력한 자료는 빈 문자열이다 — 이 값의 유무가 두 입력 방식을
           * 구분하는 유일한 신호라서, 편집 화면도 이 값을 보고 모드를 되살린다.
           */
          fileName: optionalText,
        }),
      )
      .max(20),
    /** Day the case went public; feeds `datePublished` and the list card */
    publishedAt: optionalIsoDate,
    /** Day the figures were last checked against the customer; feeds `dateModified` */
    lastVerifiedAt: optionalIsoDate,
    /** The customer agreed to be named (or aliased) as written in `customerName` */
    customerDisclosureConfirmed: confirmation,
    /** Deeply holds the right to show the customer's logo or imagery */
    logoUsageConfirmed: confirmation,
  };
}

/**
 * ### industrialCaseDocumentSchema
 *
 * Validates a document that may be published. New metadata defaults preserve
 * historic documents, while disclosure confirmations protect new publication.
 */
export const industrialCaseDocumentSchema = z.object(
  createIndustrialCaseDocumentShape('published'),
);

/**
 * ### industrialCaseDraftDocumentSchema
 *
 * Validates an editable document without requiring content completeness.
 * Structural constraints still apply so a saved draft can always reopen.
 */
export const industrialCaseDraftDocumentSchema = z.object(
  createIndustrialCaseDocumentShape('draft'),
);

/**
 * ### IndustrialCaseDocument
 *
 * Fully normalized customer-case data after schema defaults have been applied.
 */
export type IndustrialCaseDocument = z.infer<
  typeof industrialCaseDocumentSchema
>;
/**
 * ### IndustrialCaseMetric
 *
 * A displayed result paired with its definition, measurement scope, and source.
 */
export type IndustrialCaseMetric = IndustrialCaseDocument['primaryMetric'];
/**
 * ### IndustrialCaseAccuracyPoint
 *
 * One retained point from a legacy accuracy figure.
 */
export type IndustrialCaseAccuracyPoint = z.infer<
  typeof industrialCaseAccuracyPointSchema
>;

/**
 * ### parseIndustrialCaseDocument
 *
 * Parses an unknown board payload against the publication contract.
 *
 * @param input Untrusted persisted or form-derived document data
 * @returns Parsed publication-ready document or validation issues
 */
export function parseIndustrialCaseDocument(input: unknown) {
  return industrialCaseDocumentSchema.safeParse(input);
}

/**
 * ### parseIndustrialCaseDraftDocument
 *
 * Parses an unknown board payload as an editable draft, applying defaults for
 * metadata that older documents did not store.
 *
 * @param input Untrusted persisted or form-derived document data
 * @returns Parsed draft document or validation issues
 */
export function parseIndustrialCaseDraftDocument(input: unknown) {
  return industrialCaseDraftDocumentSchema.safeParse(input);
}

/**
 * ### selectDisplayedIndustrialCaseOutcomes
 *
 * Narrows a document's outcomes to the ones the public page shows.
 *
 * A document published under the old six-metric limit still parses (see
 * {@link INDUSTRIAL_CASE_OUTCOME_LIMIT}), so the renderer, not the parser,
 * applies the current limit. Nothing is removed from the stored document.
 *
 * @param outcomes Every outcome stored on the document
 * @returns The first {@link INDUSTRIAL_CASE_OUTCOME_LIMIT} outcomes
 */
export function selectDisplayedIndustrialCaseOutcomes<
  Outcome extends IndustrialCaseMetric,
>(outcomes: ReadonlyArray<Outcome>): Array<Outcome> {
  return outcomes.slice(0, INDUSTRIAL_CASE_OUTCOME_LIMIT);
}

/**
 * ### hasIndustrialCaseBeforeAfter
 *
 * Reports whether the comparison is complete enough to show. A single filled
 * side reads as a half-finished thought on the page, so both are required.
 *
 * @param beforeAfter The comparison as stored on the document
 * @returns Whether both sides carry text
 */
export function hasIndustrialCaseBeforeAfter(
  beforeAfter: IndustrialCaseDocument['beforeAfter'],
): boolean {
  return beforeAfter.before.trim() !== '' && beforeAfter.after.trim() !== '';
}
